import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { Add as AddIcon, Visibility as ViewIcon } from '@mui/icons-material';

import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import { fetchUsers, activateUser, deactivateUser, clearError } from '../store/slices/userSlice';
import { showSuccess, showError, showWarning, showInfo } from '../store/slices/toastSlice';
import { User, UserRole } from '../types';

interface CreateUserForm {
  username: string;
  email: string;
  password: string;
  role: string;
}

const Users: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { users, isLoading, error } = useAppSelector((state) => state.users);

  const [searchTerm, setSearchTerm] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [createForm, setCreateForm] = useState<CreateUserForm>({
    username: '',
    email: '',
    password: '',
    role: 'user',
  });

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      dispatch(showError({
        title: 'Error',
        message: error,
        duration: 5000,
      }));
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleCreateUser = () => {
    setCreateForm({
      username: '',
      email: '',
      password: '',
      role: 'user',
    });
    setSelectedUser(null);
    setCreateDialogOpen(true);
  };

  const handleCreateSubmit = () => {
    // TODO: Implement user creation
    dispatch(showInfo({
      title: 'Coming Soon',
      message: 'User creation functionality will be implemented soon.',
      duration: 4000,
    }));
    setCreateDialogOpen(false);
  };

  const handleActivateUser = async (userId: string) => {
    try {
      await dispatch(activateUser(userId)).unwrap();
      dispatch(showSuccess({
        title: 'User Activated',
        message: 'User has been successfully activated.',
        duration: 4000,
      }));
    } catch (error: any) {
      dispatch(showError({
        title: 'Activation Failed',
        message: error.message || 'Failed to activate user. Please try again.',
        duration: 5000,
      }));
    }
  };

  const handleDeactivateUser = async (userId: string) => {
    try {
      await dispatch(deactivateUser(userId)).unwrap();
      dispatch(showWarning({
        title: 'User Deactivated',
        message: 'User has been successfully deactivated.',
        duration: 4000,
      }));
    } catch (error: any) {
      dispatch(showError({
        title: 'Deactivation Failed',
        message: error.message || 'Failed to deactivate user. Please try again.',
        duration: 5000,
      }));
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'manager':
        return 'warning';
      default:
        return 'default';
    }
  };

  const filteredUsers = users.filter(user =>
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns: GridColDef[] = [
    {
      field: 'username',
      headerName: 'Username',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'first_name',
      headerName: 'First Name',
      width: 120,
      valueGetter: (params) => params.value || 'N/A',
    },
    {
      field: 'last_name',
      headerName: 'Last Name',
      width: 120,
      valueGetter: (params) => params.value || 'N/A',
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getRoleColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'error'}
          size="small"
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 150,
      valueFormatter: (params) => new Date(params.value).toLocaleDateString(),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Button
            size="small"
            onClick={() => navigate(`/users/${params.row.id}`)}
            startIcon={<ViewIcon />}
            sx={{ mr: 1 }}
          >
            View
          </Button>
          {params.row.is_active ? (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={() => handleDeactivateUser(params.row.id)}
            >
              Deactivate
            </Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              color="success"
              onClick={() => handleActivateUser(params.row.id)}
            >
              Activate
            </Button>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          User Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateUser}
        >
          Create User
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Search users by username, email, or role..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            size="small"
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <DataGrid
            rows={filteredUsers}
            columns={columns}
            loading={isLoading}
            autoHeight
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            disableRowSelectionOnClick
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Username"
                value={createForm.username}
                onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Role"
                value={createForm.role}
                onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateSubmit} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;
